import classNames from "classnames";
import React, { useState, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LoadingSpinner from "../../atoms/loadingSpinner/LoadingSpinner";
import CollapsiblePanel from "../collapsible-panel/CollapsiblePanel";
import Icon from "../icon/Icon";
import FilterTabs from "./FilterTabs";
import "./styles.styl";
import { useDeliveries } from "./useDeliveries";

const FILTERS = [
  { id: "not_yet_invoiced", labelId: "pastDeliveriesOpen" },
  { id: "invoiced", labelId: "pastDeliveriesInvoiced" },
  { id: "paused_or_cancelled", labelId: "orderPausedCancelled" }
];

const SKELETON_CARD_COUNT = 2;

export default function PastDeliveries({
  subscription,
  displayed,
  onCloseClick,
  delivery: currentDelivery,
}) {
  const intl = useIntl();
  const [activeFilter, setActiveFilter] = useState("invoiced");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    deliveries,
    isLoading,
    pagination,
    formatters,
    handleInvoiceClick,
  } = useDeliveries({
    subscription,
    displayed,
    endpoint: "past-deliveries",
    page: currentPage,
    category: activeFilter
  });

  const handleDeliveryClick = useCallback((delivery) => {
    if (delivery.id && delivery.id === currentDelivery?.id) {
      onCloseClick();
    } else if (delivery.id) {
      window.open(`/view/delivery/${delivery.id}`, "_blank");
    }
  }, [currentDelivery?.id, onCloseClick]);

  const handleShowMore = useCallback(() => {
    setCurrentPage(prev => prev + 1);
  }, []);

  const handleFilterChange = useCallback((filterId) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
  }, []);


  const getDeliveryStatus = (delivery, isInvoiced) => {
    if (delivery.status === "paused") {
      return "paused";
    } else if (delivery.status === "cancelled") {
      return "cancelled";
    } else if (isInvoiced || delivery.status === "sold") {
      return "delivered";
    }
    return null;
  };

  const isGrouped = useMemo(() =>
    deliveries?.length > 0 && "deliveries" in deliveries[0], 
    [deliveries]
  );

  if (!displayed) return null;

  const renderDeliveryCard = (delivery, isInvoiced) => {
    const status = getDeliveryStatus(delivery, isInvoiced);

    return (
      <div
        key={delivery.id}
        className={classNames("delivery-card", {
          "is-current": delivery.id === currentDelivery?.id,
        })}
        onClick={() => handleDeliveryClick(delivery)}
      >
        <div className="weekday-info">
          <div className="weekday">
            {formatters.formatWeekday(
              delivery.specific_timeslot_weekday,
              delivery.generic_timeslot_weekday,
              false
            )}
          </div>
          <div className="delivery-address">
            {delivery.address_display ||
             intl.formatMessage({ id: "checkDeliveryArea" })}
          </div>
        </div>

        <div className="date-col">
          <div className="date">
            {formatters.formatDate(delivery.timeslot__date)}
          </div>
        </div>

        <div className="delivery-actions">
          {status && (
            <div className={classNames("status-pill", status)}>
              <FormattedMessage id={`deliveryStatus.${status}`} />
            </div>
          )}
          <Icon name="shopping_cart" className="cart-icon" />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading && currentPage === 1) {
      return (
        <div className="delivery-loading-container">
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <div key={i} className="skeleton-delivery-card">
              <div className="skeleton-weekday-info">
                <div className="skeleton-line skeleton-weekday"></div>
                <div className="skeleton-line skeleton-address"></div>
              </div>
              <div className="skeleton-date-col">
                <div className="skeleton-line skeleton-date"></div>
              </div>
              <div className="skeleton-delivery-actions">
                <div className="skeleton-line skeleton-status-pill"></div>
                <div className="skeleton-line skeleton-cart-icon"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (!deliveries?.length) {
      return (
        <div className="no-deliveries">
          <FormattedMessage
            id={activeFilter === "paused_or_cancelled" ? "noDeliveriesInCategory" : "noEntriesAvailable"}
            defaultMessage={activeFilter === "paused_or_cancelled" ? "No deliveries in this category" : "No entries available"}
          />
        </div>
      );
    }

    const flatListFilters = ["not_yet_invoiced", "paused_or_cancelled"];
    if (!isGrouped || flatListFilters.includes(activeFilter)) {
      const deliveriesToRender = isGrouped
        ? deliveries.flatMap(group => group.deliveries)
        : deliveries;

      return (
        <>
          <div className="deliveries-wrapper flat-list no-grouping">
            {deliveriesToRender.map((delivery) => renderDeliveryCard(delivery, false))}
          </div>
          {renderPagination()}
        </>
      );
    }

    // Render grouped deliveries (invoiced filter)
    return (
      <>
        {deliveries.map((group) => {
          const isInvoiced = group.is_invoiced;
          const firstDelivery = group.deliveries?.[0];

          const totalPrice = isInvoiced && firstDelivery?.invoice_total_gross
            ? firstDelivery.invoice_total_gross
            : null;

          return (
            <CollapsiblePanel
              key={group.group_text || group.group_date}
              defaultOpen={true}
              title={
                <div className="invoice-header">
                  {isInvoiced ? (
                    <>
                      <span className="invoice-number">{firstDelivery.invoice_number}</span>

                        <span className="invoice-amount">
                          {totalPrice > 0 && (
                            <>
                              <FormattedMessage id="summe" defaultMessage="Total" /> {formatters.formatPrice(totalPrice)}
                            </>
                          )}
                        </span>

                      {(firstDelivery?.invoice_url || firstDelivery?.invoice_gcloud_path) && (
                        <a
                          href="#"
                          className="invoice-link-tab"
                          onClick={(e) => handleInvoiceClick(firstDelivery, e)}
                        >
                          <FormattedMessage id="rechnung" defaultMessage="Rechnung" />
                        </a>
                      )}
                    </>
                  ) : (
                    <span className="date-range">
                      {formatters.formatDate(group.group_date)}
                    </span>
                  )}
                </div>
              }
            >
              <div className="deliveries-wrapper">
                {group.deliveries?.map((delivery) =>
                  renderDeliveryCard(delivery, isInvoiced)
                )}
              </div>
            </CollapsiblePanel>
          );
        })}

        {renderPagination()}
      </>
    );
  };

  const renderPagination = () => (
    <>
      {pagination?.next && (
        <div className="show-more">
          <button
            className="show-more-button"
            onClick={handleShowMore}
            disabled={isLoading}
          >
            {isLoading && currentPage > 1 ? (
              <div className="loading-more">
                <LoadingSpinner isLoading={true} clazzName="rule__spinner" />
              </div>
            ) : (
              <FormattedMessage id="showMore" defaultMessage="Show More" />
            )}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="past-deliveries">
      <FilterTabs
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        filters={FILTERS}
        showClearFilter={false}
      />

      <div className="delivery-content">
        {renderContent()}
      </div>
    </div>
  );
}
